import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PauseReason,
  PauseReasonCreateBody,
  PauseReasonUpdateBody,
  Presence,
  PresenceSetBody,
  PresenceSettings,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { PAUSE_REASONS_KEY, PRESENCE_KEY, PRESENCE_SETTINGS_KEY } from './presence-queries';

/** troca o PRÓPRIO status; o chip atualiza já, a equipe pelo evento realtime */
export function useSetMyPresence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PresenceSetBody) => {
      const res = await api<Presence>('/presence/me', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData([...PRESENCE_KEY, 'me'], data);
      void qc.invalidateQueries({ queryKey: PRESENCE_KEY });
    },
  });
}

/**
 * spec 072: o aviso de atividade da aba. Sem retry nem estado na tela: perder
 * um aviso custa no máximo um minuto de atividade, e o próximo gesto avisa.
 */
export async function sendPresenceActivity(): Promise<void> {
  await api<void>('/presence/me/activity', { method: 'POST' });
}

/** spec 072: o admin muda o limite de inatividade da organização */
export function useSetPresenceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PresenceSettings) => {
      const res = await api<PresenceSettings>('/presence/settings', { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => qc.setQueryData(PRESENCE_SETTINGS_KEY, data),
  });
}

/** admin encerra o status aberto de alguém no horário real (ISO) */
export function useEndPresence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, endedAt }: { userId: number; endedAt: string }) => {
      const res = await api<Presence>(`/presence/${userId}/end`, { method: 'POST', body: { endedAt } });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PRESENCE_KEY }),
  });
}

// ── Motivos de pausa (configuração) ─────────────────────────────────────────

export function useCreatePauseReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PauseReasonCreateBody) => {
      const res = await api<PauseReason>('/presence/pause-reasons', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PAUSE_REASONS_KEY }),
  });
}

export function useUpdatePauseReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & PauseReasonUpdateBody) => {
      const res = await api<PauseReason>(`/presence/pause-reasons/${id}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PAUSE_REASONS_KEY }),
  });
}

export function useDeletePauseReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/presence/pause-reasons/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PAUSE_REASONS_KEY }),
  });
}

export function useReorderPauseReasons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await api<PauseReason[]>('/presence/pause-reasons/order', { method: 'PUT', body: { ids } });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PAUSE_REASONS_KEY }),
  });
}
